import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { Plus } from 'lucide-react';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import CertificateSavePanel from '@/shein-platform/components/CertificateSavePanel';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import { useSheinPageShopScope } from '@/shein-platform/hooks/useSheinPageShopScope';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { extractSheinList, pickField } from '@/shein-platform/utils/sheinResponse';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { showToast } from '@/utils/toast';
import ComplianceAutoBindRuleModal from '@/shein-platform/modules/compliance/ComplianceAutoBindRuleModal';
import {
  readComplianceAutoBindErrors,
} from '@/shein-platform/modules/compliance/complianceAutoBindRules';
import {
  maybeRunCertAutoBindAfterSync,
} from '@/shein-platform/modules/compliance/complianceAutoBindRunner';
import { loadProductMetaBySkc } from '@/shein-platform/utils/sheinProductMetaBySkc';
import { createPortal } from 'react-dom';
import '@/styles/shein-platform/shein-product-ops.css';

const FILTER_ALL = 'all';
const FILTER_MISSING = '0';
const FILTER_PENDING = '1';
const FILTER_APPROVED = '2';
const FILTER_REJECTED = '3';

/**
 * 状态筛选项：对齐 requirements/list 的 reviewState
 * 0 未审核(缺失) / 1 待审核 / 2 审核成功 / 3 审核驳回
 */
const FILTER_OPTIONS = [
  { id: FILTER_ALL, label: '全部' },
  { id: FILTER_MISSING, label: '未审核' },
  { id: FILTER_PENDING, label: '待审核' },
  { id: FILTER_APPROVED, label: '审核成功' },
  { id: FILTER_REJECTED, label: '审核驳回' },
];

const REVIEW_STATE_LABELS = {
  0: '未审核',
  1: '待审核',
  2: '审核成功',
  3: '审核驳回',
};

function resolveInitialFilter(value) {
  const raw = value == null ? '' : String(value).trim().toLowerCase();
  // 兼容旧链接
  if (raw === 'todo' || raw === 'missing' || raw === 'required') return FILTER_MISSING;
  if (raw === 'pending') return FILTER_PENDING;
  if (raw === 'approved') return FILTER_APPROVED;
  if (raw === 'rejected') return FILTER_REJECTED;
  const allowed = new Set(FILTER_OPTIONS.map((t) => t.id));
  if (value != null && allowed.has(String(value))) return String(value);
  return FILTER_ALL;
}

const ID_TYPE_OPTIONS = [
  { value: 'skc', label: 'SKC' },
  { value: 'spu', label: 'SPU' },
];

/**
 * 表头对齐 requirements/list（ZSZZL）+ 产品信息补全。
 */
const TABLE_COLUMNS = [
  { key: 'check', label: '', platformField: null, width: '50px' },
  { key: 'product', get label() { return fixedUi("产品信息"); }, platformField: 'product(title/image) + skcName', width: '30%' },
  { key: 'certificateTypeCode', label: '缺失证书编码', platformField: 'certificateTypeCode', width: '17.5%' },
  { key: 'certificateTypeName', label: '缺失证书名称', platformField: 'certificateTypeName', width: '17.5%' },
  { key: 'status', get label() { return fixedUi("审核状态"); }, platformField: 'reviewState', width: '17.5%' },
  { key: 'ops', get label() { return fixedUi("操作"); }, platformField: null, width: '17.5%' },
];

const COL_SPAN = TABLE_COLUMNS.length;

const EMPTY_FILTERS = {
  idType: 'skc',
  idValue: '',
  certificateTypeId: '',
  status: FILTER_ALL,
};

const EMPTY_QUERY = {
  idType: 'skc',
  keywords: '',
  certificateTypeId: '',
  status: FILTER_ALL,
};

function parseTypeOptions(res) {
  const list = res?.certificateTypeInfoList
    || res?.data?.certificateTypeInfoList
    || res?.info?.certificateTypeInfoList
    || [];
  return Array.isArray(list) ? list.filter((item) => Number(item?.certificateTypeId) !== 844) : [];
}

/** 平台 reviewState：0 未审核(缺失)、1 待审核、2 审核成功、3 审核驳回 */
function platformStatusLabel(row) {
  const review = row?.reviewState;
  if (review === 0 || review === '0') return REVIEW_STATE_LABELS[0];
  if (review === 1 || review === '1') return REVIEW_STATE_LABELS[1];
  if (review === 2 || review === '2') return REVIEW_STATE_LABELS[2];
  if (review === 3 || review === '3') return REVIEW_STATE_LABELS[3];
  const todo = pickField(row, 'todoStatus');
  if (todo === 'MISSING' || row?.missStatus === true) return REVIEW_STATE_LABELS[0];
  if (todo === 'PENDING_AUDIT') return REVIEW_STATE_LABELS[1];
  if (todo === 'APPROVED') return REVIEW_STATE_LABELS[2];
  if (todo === 'REJECTED') return REVIEW_STATE_LABELS[3];
  return '—';
}


const REVIEW_TEXT_KEYS = { '未审核': 'certList.review0', '待审核': 'certList.review1', '审核成功': 'certList.review2', '审核驳回': 'certList.review3' };
function reviewStatusText(row) {
  const raw = platformStatusLabel(row);
  const id = REVIEW_TEXT_KEYS[raw];
  return id ? i18n.t(id, { ns: 'workbench' }) : raw;
}
function platformStatusModifier(row) {
  const review = Number(row?.reviewState);
  if (review === 2) return 'success';
  if (review === 0 || review === 3) return 'failed';
  if (review === 1) return 'pending';
  const label = platformStatusLabel(row);
  if (label === REVIEW_STATE_LABELS[2]) return 'success';
  if (label === REVIEW_STATE_LABELS[0] || label === REVIEW_STATE_LABELS[3]) return 'failed';
  return 'pending';
}

function isUnreviewed(row) {
  const review = row?.reviewState;
  if (review === 0 || review === '0') return true;
  if (review == null || review === '') {
    const todo = pickField(row, 'todoStatus');
    return todo === 'MISSING' || row?.missStatus === true || !todo;
  }
  return false;
}

function bindActionLabel(row) {
  return isUnreviewed(row) ? i18n.t('certList.bind', { ns: 'workbench' }) : i18n.t('certList.rebind', { ns: 'workbench' });
}

/** 多值拆成竖排列；支持数组或 · 、 , ; 分隔的字符串 */
function toStackItems(...candidates) {
  for (const raw of candidates) {
    if (Array.isArray(raw)) {
      const items = raw
        .map((v) => String(v ?? '').trim())
        .filter(Boolean);
      if (items.length) return items;
      continue;
    }
    if (raw == null) continue;
    const text = String(raw).trim();
    if (!text) continue;
    const parts = text.split(/\s*[·、,;|]\s*/).map((s) => s.trim()).filter(Boolean);
    return parts.length ? parts : [text];
  }
  return ['—'];
}

function certificateTypeCodesOf(row) {
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : null;
  return toStackItems(
    row?.certificateTypeCodes,
    payload?.certificateTypeCodes,
    pickField(row, 'certificateTypeCode', 'typeCode'),
    payload?.certificateTypeCode,
  );
}

function certificateTypeNamesOf(row) {
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : null;
  return toStackItems(
    row?.certificateTypeNames,
    payload?.certificateTypeNames,
    pickField(row, 'certificateTypeName', 'certificateTypeValue', 'certificateName', 'certificateType'),
    payload?.certificateTypeName,
  );
}

function CellStack({ items, itemClassName }) {
  const list = Array.isArray(items) && items.length ? items : ['—'];
  return (
    <div className="spo-cell-stack" title={list.join('、')}>
      {list.map((text, idx) => (
        <div key={`${idx}-${text}`} className={itemClassName || 'spo-cell-stack__item'}>
          {text}
        </div>
      ))}
    </div>
  );
}

function productMetaOf(row, metaBySkc = {}) {
  const product = row?.product && typeof row.product === 'object' ? row.product : null;
  const skc = pickField(row, 'skcName', 'skcNames') || product?.skcName || '';
  const fromMap = skc && metaBySkc[skc] ? metaBySkc[skc] : {};
  return {
    skc,
    spu: pickField(row, 'spuName') || product?.spuName || fromMap.spuName || '',
    title: pickField(row, 'productTitle') || product?.title || fromMap.title || '',
    imageUrl: pickField(row, 'productImageUrl') || product?.imageUrl || fromMap.imageUrl || '',
    supplierCode: fromMap.supplierCode || pickField(row, 'supplierCode', 'supplier_code') || product?.supplierCode || '',
  };
}

function rowKey(row, index) {
  return pickField(row, 'certificateId', 'id', 'poolSn', 'certificateNo', 'certificatePoolId') || `cert-${index}`;
}

function selectionId(row, index) {
  return pickField(row, 'certificateId') || rowKey(row, index);
}

/** 商品证书：对齐 goods-compliance-requirements/list（ZSZZL） */
export default function CertificateListPage({
  initialFilter = FILTER_ALL,
  embedded: _embedded = false,
  syncKinds = ['certificate', 'certAutoBind'],
  active = true,
} = {}) {
  const { t } = useTranslation('workbench');
  const {
    activeShop,
    activeShopId,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || (allShopsScope ? '' : (activeShop?.id || activeShopId));

  const [filterDraft, setFilterDraft] = useState(() => ({
    ...EMPTY_FILTERS,
    status: resolveInitialFilter(initialFilter),
  }));
  const [query, setQuery] = useState(() => ({
    ...EMPTY_QUERY,
    status: resolveInitialFilter(initialFilter),
  }));
  const [queryNonce, setQueryNonce] = useState(0);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [seed, setSeed] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [productMetaBySkc, setProductMetaBySkc] = useState({});
  const [autoBindErrors, setAutoBindErrors] = useState({});
  const [autoBindErrorSkc, setAutoBindErrorSkc] = useState('');
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  const load = useCallback(async () => {
    if (!active) return;
    if (!shopId) {
      setRows([]);
      setTotal(0);
      setProductMetaBySkc({});
      return;
    }
    setLoading(true);
    try {
      const res = await sheinPlatformAPI.listCertificates(shopId, {
        pageNum: page,
        pageSize,
        source: 'cached',
        filter: query.status || FILTER_ALL,
        idType: query.idType || 'spu',
        keywords: query.keywords || undefined,
        certificateTypeId: query.certificateTypeId || undefined,
      });
      const list = extractSheinList(res);
      setRows(list);
      setTotal(Number(res?.total) || 0);
      const skcs = list
        .map((row) => String(pickField(row, 'skcName', 'skcNames') || '').trim())
        .filter(Boolean);
      if (skcs.length) {
        const meta = await loadProductMetaBySkc(shopId, skcs, { enrichCategoryPath: false });
        setProductMetaBySkc(meta);
      } else {
        setProductMetaBySkc({});
      }
      // 跨页选中：翻页/查询刷新列表时保留已选 certificateId
    } catch (e) {
      setRows([]);
      setTotal(0);
      setProductMetaBySkc({});
      showToast(e?.message || t('certList.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, page, pageSize, query.status, query.idType, query.keywords, query.certificateTypeId, queryNonce, active, t]);

  useEffect(() => {
    if (!active) return;
    load();
  }, [load, active]);

  useEffect(() => {
    const status = resolveInitialFilter(initialFilter);
    setFilterDraft({ ...EMPTY_FILTERS, status });
    setQuery({ ...EMPTY_QUERY, status });
    setBatchModalOpen(false);
    setPage(1);
    setQueryNonce((n) => n + 1);
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    setSelectedKeys(new Set());
    setTypeOptions([]);
  }, [shopId, initialFilter]);

  useEffect(() => {
    if (!shopId || !active) return undefined;
    let cancelled = false;
    sheinPlatformAPI.listCertificateTypes(shopId)
      .then((res) => {
        if (!cancelled) setTypeOptions(parseTypeOptions(res));
      })
      .catch(() => {
        if (!cancelled) setTypeOptions([]);
      });
    return () => { cancelled = true; };
  }, [shopId, active]);

  useEffect(() => {
    if (!shopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopSyncStatus(shopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const cert = modules.find((m) => String(m.module || '').toUpperCase() === 'CERTIFICATE');
        const at = cert?.lastSyncEndAt || cert?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [shopId]);

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onQuery = () => {
    const tokens = parseBatchTokens(filterDraft.idValue).slice(0, BATCH_QUERY_LIMIT);
    if (shopId) setLoading(true);
    setPage(1);
    setQueryNonce((n) => {
      const next = n + 1;
      setQuery({
        idType: filterDraft.idType || 'spu',
        keywords: tokens.join(','),
        certificateTypeId: filterDraft.certificateTypeId || '',
        status: resolveInitialFilter(filterDraft.status),
        _nonce: next,
      });
      return next;
    });
  };

  const onReset = () => {
    const status = FILTER_ALL;
    setFilterDraft({ ...EMPTY_FILTERS, status });
    setQuery({ ...EMPTY_QUERY, status });
    setPage(1);
    setSelectedKeys(new Set());
    setQueryNonce((n) => n + 1);
  };

  const syncSpuName = (() => {
    if (query.idType !== 'spu' || !query.keywords) return '';
    const tokens = parseBatchTokens(query.keywords);
    return tokens.length === 1 ? tokens[0] : '';
  })();

  const syncCertificatesAction = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
      return;
    }
    const payload = syncSpuName ? { spuName: syncSpuName } : {};
    const track = await startJob({
      kind: 'certificate',
      shopId,
      payload,
      label: t('publishRecord.syncUpdate'),
      silent: true,
    });
    if (!track?.id) return;
    await new Promise((resolve) => {
      const unsub = onTrackDone(track.id, async (result) => {
        unsub();
        if (result?.ok) {
          const view = result.view || {};
          const count = Number(view.imported) || 0;
          const todos = Number(view.todoCount) || 0;
          setLastSyncedAt(String(view.finishedAt || new Date().toISOString()));
          let toastMsg = count > 0
            ? t('certList.syncDone', { count, todos })
            : (view.message || t('certList.syncEmpty'));
          try {
            const auto = await maybeRunCertAutoBindAfterSync(shopId);
            if (auto?.ok > 0) {
              toastMsg = `${toastMsg}；${t('certList.autoBound', { count: auto.ok })}`;
            }
            if (auto?.fail > 0 || Object.keys(auto?.errorsBySkc || {}).length) {
              const prefRes = await sheinPlatformAPI.getPreferences().catch(() => null);
              const prefs = prefRes?.preferences || prefRes || {};
              setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'certificate'));
            }
          } catch {
            /* 自动绑定失败不影响同步提示 */
          }
          showToast(toastMsg, count > 0 ? 'success' : 'warning');
          setPage(1);
          await load();
        } else {
          const err = result?.view?.error || result?.view?.message || result?.error || t('certList.syncFailed');
          showToast(err, 'error');
        }
        resolve();
      });
    });
  }, [shopId, syncSpuName, load, startJob, onTrackDone, t]);

  const {
    refresh: onSyncCertificates,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(syncCertificatesAction);

  const [seedingDemo, setSeedingDemo] = useState(false);
  const [autoBindModalOpen, setAutoBindModalOpen] = useState(false);

  useEffect(() => {
    if (!shopId || !active) {
      setAutoBindErrors({});
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getPreferences()
      .then((res) => {
        if (cancelled) return;
        const prefs = res?.preferences || res || {};
        setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'certificate'));
      })
      .catch(() => {
        if (!cancelled) setAutoBindErrors({});
      });
    return () => { cancelled = true; };
  }, [shopId, active]);

  const handleSeedDemo = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
      return;
    }
    setSeedingDemo(true);
    try {
      const res = await sheinPlatformAPI.seedCertificateDemo(shopId);
      const count = Array.isArray(res?.certificateIds)
        ? res.certificateIds.length
        : (Number(res?.cacheUpserted) || 0);
      showToast(count > 0 ? t('certList.demoWritten', { count }) : t('certList.demoSeeded'), 'success');
      setPage(1);
      setFilterDraft({ ...EMPTY_FILTERS, status: FILTER_ALL });
      setQuery({ ...EMPTY_QUERY, status: FILTER_ALL });
      setQueryNonce((n) => n + 1);
      await load();
    } catch (e) {
      showToast(e?.message || t('certList.demoFailed'), 'error');
    } finally {
      setSeedingDemo(false);
    }
  }, [shopId, load, t]);

  const syncing = syncingLocal || isBusy('certificate', shopId);

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const idTypeLabel = ID_TYPE_OPTIONS.find((o) => o.value === filterDraft.idType)?.label || 'SKC';

  const selectableKeys = useMemo(
    () => rows.map((row, index) => selectionId(row, index)).filter(Boolean),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openUpload = (row = null) => {
    if (row) {
      setSeed({
        spuName: pickField(row, 'spuName'),
        skcName: pickField(row, 'skcName'),
        skcNames: pickField(row, 'skcNames'),
        certificateTypeId: pickField(row, 'certificateTypeId'),
        certificateTypeCode: pickField(row, 'certificateTypeCode'),
        certificateDimension: pickField(row, 'certificateDimension') || 1,
        certificatePoolId: pickField(row, 'certificatePoolId'),
        poolSn: pickField(row, 'poolSn'),
        _ts: Date.now(),
      });
    } else {
      setSeed(null);
    }
    setPanelOpen(true);
  };

  const openBatchBind = () => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row, index) => {
      const key = selectionId(row, index);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
    const selected = Array.from(selectedKeys)
      .map((key) => map.get(key))
      .filter(Boolean);
    if (!selected.length) {
      showToast(t('certList.pickBind'), 'warning');
      return;
    }
    const skcs = [...new Set(
      selected
        .map((row) => pickField(row, 'skcName', 'skc', 'skcCode'))
        .filter(Boolean)
        .map((s) => String(s).trim()),
    )];
    if (!skcs.length) {
      showToast(t('certList.missingSkc'), 'error');
      return;
    }
    const first = selected[0];
    setSeed({
      skcName: skcs.join(','),
      skcNames: skcs.join(','),
      certificateTypeId: pickField(first, 'certificateTypeId'),
      certificateTypeCode: pickField(first, 'certificateTypeCode'),
      certificateDimension: 1,
      _ts: Date.now(),
    });
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setSeed(null);
  };

  const resolveSelectedRows = () => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row, index) => {
      const key = selectionId(row, index);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  };

  const writeCertificatesCsv = (exportRows, filename) => {
    if (!exportRows.length) {
      showToast(t('certLib.noneExport'), 'warning');
      return;
    }
    downloadCsv(
      filename,
      ['skc', 'spu', 'title', 'certificateTypeCode', 'certificateTypeName', 'status'],
      exportRows.map((row) => {
        const product = productMetaOf(row, productMetaBySkc);
        return {
          skc: product.skc,
          spu: product.spu,
          title: product.title,
          certificateTypeCode: certificateTypeCodesOf(row).join('、'),
          certificateTypeName: certificateTypeNamesOf(row).join('、'),
          status: platformStatusLabel(row),
        };
      }),
    );
    showToast(t('certLib.exported', { count: exportRows.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await sheinPlatformAPI.listCertificates(shopId, {
          pageNum: pageNo,
          pageSize: size,
          source: 'cached',
          filter: query.status || FILTER_ALL,
          idType: query.idType || 'spu',
          keywords: query.keywords || undefined,
          certificateTypeId: query.certificateTypeId || undefined,
        });
        const list = extractSheinList(res);
        return { items: list, total: Number(res?.total) || 0 };
      });
      writeCertificatesCsv(all, `shein-certificates-filtered-${Date.now()}.csv`);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportPage = () => {
    writeCertificatesCsv(rows, `shein-certificates-page-${Date.now()}.csv`);
  };

  const exportSelected = () => {
    const selected = resolveSelectedRows();
    if (!selected.length) {
      showToast(t('certLib.pickExport'), 'warning');
      return;
    }
    writeCertificatesCsv(selected, `shein-certificates-selected-${Date.now()}.csv`);
  };

  const onBatchDelete = async () => {
    if (!shopId || selectedKeys.size === 0) {
      showToast(t('certList.pickDeleteCache'), 'warning');
      return;
    }
    const ids = Array.from(selectedKeys).filter(Boolean);
    if (ids.length === 0) {
      showToast(t('certList.missingCertId'), 'error');
      return;
    }
    const ok = await showSheinConfirm({
      title: t('returnList.batchDelete'),
      message: t('certList.batchDeleteMsg', { count: ids.length, platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await sheinPlatformAPI.batchDeleteCertificates(shopId, ids);
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('certList.deletedCache', { count }), 'success');
      } else {
        showToast(t('certList.noneDeletedCache'), 'warning');
      }
      setSelectedKeys(new Set());
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <SheinSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || deleting || seedingDemo || exporting}
            syncKinds={syncKinds}
            quotaKind="certificate"
            onSync={() => {
              if (!shopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
                return;
              }
              onSyncCertificates();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('certList.syncHintShein')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId || deleting}
            extraActions={[
              {
                key: 'auto-rules',
                label: t('certList.autoRule'),
                disabled: !shopId || deleting || seedingDemo || syncing || loading,
                onClick: () => setAutoBindModalOpen(true),
              },
              {
                key: 'batch-bind',
                label: selectedKeys.size > 0 ? t('certList.batchBindCount', { count: selectedKeys.size }) : t('certList.batchBind'),
                tone: 'primary',
                disabled: !shopId || deleting || selectedKeys.size === 0,
                onClick: openBatchBind,
              },
              ...(process.env.NODE_ENV !== 'production'
                ? [{
                  key: 'seed-demo',
                  label: seedingDemo ? t('certList.seeding') : t('certList.loadDemo'),
                  disabled: !shopId || seedingDemo || syncing,
                  onClick: handleSeedDemo,
                }]
                : []),
            ]}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch">
              <select
                className="spo-select spo-select--compact"
                value={filterDraft.idType}
                disabled={!shopId}
                onChange={(e) => updateField('idType', e.target.value)}
                aria-label={t('tiktokCatalog.idTypeAria')}
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.id === 'all' ? 'tiktokCatalog.all' : ('certList.review' + opt.id), { defaultValue: opt.label })}</option>
                ))}
              </select>
              <input
                className="spo-input"
                value={filterDraft.idValue}
                placeholder={t('tiktokCatalog.idPlaceholder', { label: idTypeLabel })}
                disabled={!shopId}
                onChange={(e) => updateField('idValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('tiktokCatalog.batchInput', { label: idTypeLabel })}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchModalOpen(true)}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="spo-field spo-field--inline spo-field--cert-type">
              <span className="spo-field__label">{t('certLib.colType')}</span>
              <select
                className="spo-select spo-select--cert-type"
                value={filterDraft.certificateTypeId}
                disabled={!shopId}
                onChange={(e) => updateField('certificateTypeId', e.target.value)}
                aria-label={t('certLib.colType')}
              >
                <option value="">{t('certLib.allTypes')}</option>
                {typeOptions.map((item) => (
                  <option key={item.certificateTypeId} value={String(item.certificateTypeId)}>
                    {item.certificateType || item.certificateTypeName || item.certificateTypeId}
                  </option>
                ))}
              </select>
            </div>

            <div className="spo-field spo-field--inline spo-field--status">
              <span className="spo-field__label">{t('certList.colAudit')}</span>
              <select
                className="spo-select spo-select--status"
                value={filterDraft.status || FILTER_ALL}
                disabled={!shopId}
                onChange={(e) => updateField('status', e.target.value)}
                aria-label={t('certList.certStatusAria')}
              >
                {FILTER_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>{t(opt.id === 'all' ? 'tiktokCatalog.all' : ('certList.review' + opt.id), { defaultValue: opt.label })}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="spo-filter-bar__actions">
            <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onReset} disabled={!shopId}>
              {t('returnList.reset')}
            </button>
            <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onQuery} disabled={!shopId}>
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <BatchQueryModal
        open={batchModalOpen}
        title={t('certList.batchQueryId', { label: idTypeLabel })}
        initialValue={filterDraft.idValue}
        placeholder={t('certList.batchLine', { label: idTypeLabel })}
        onConfirm={(tokens) => {
          updateField('idValue', joinBatchTokens(tokens));
        }}
        onClose={() => setBatchModalOpen(false)}
      />

      <CertificateSavePanel
        open={panelOpen}
        shopId={shopId}
        seed={seed}
        onSaved={() => {
          closePanel();
          load();
        }}
        onClose={closePanel}
      />

      <ProductListCard tabs={shopDimensionTabs} loading={Boolean(shopId) && loading} loadingLabel={t('certList.querying')}>
        <div className="spo-drafts__table-wrap spo-certs-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-certs-table">
            <colgroup>
              {TABLE_COLUMNS.map((col) => (
                <col
                  key={col.key}
                  className={`spo-certs-col spo-certs-col--${col.key}`}
                  style={col.width ? { width: col.width } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-certs-th spo-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('certLib.selectAll')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {TABLE_COLUMNS.filter((c) => c.key !== 'check').map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={`spo-certs-th spo-certs-th--${col.key}`}
                    title={col.platformField || undefined}
                  >
                    {i18n.t({ product: 'certList.colProduct', certificateTypeCode: 'certList.colMissingCode', certificateTypeName: 'certList.colMissingName', status: 'certList.colAudit', certName: 'certList.colQualName', uploadStatus: 'certList.colUpload', certNameCn: 'certList.colQualCn', impact: 'certList.colImpact', ops: 'certLib.colOps' }[col.key], { ns: 'workbench', defaultValue: col.label })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_SPAN} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">
                        {!shopId
                          ? (allShopsScope ? t('draftBox.pickConcrete') : t('returnList.pickShop'))
                          : loading
                            ? t('returnList.loading')
                            : query.status === FILTER_MISSING
                              ? t('certList.emptyMissing')
                              : t('certList.emptyData')}
                      </p>
                      {!shopId || loading ? null : (
                        <p className="spo-certs-empty__desc">
                          {query.status === FILTER_MISSING
                            ? t('certList.emptyDemoPullShein')
                            : t('certList.emptyDemoFilterShein')}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const key = selectionId(row, index);
                  const product = productMetaOf(row, productMetaBySkc);
                  return (
                    <tr key={rowKey(row, index)}>
                      <td
                        className="spo-certs-td spo-certs-td--check"
                        onClick={() => toggleOne(key)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('returnList.selectRow', { title: product.skc || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="spo-certs-td spo-certs-td--product">
                        <div className="spo-photo-product">
                          {product.imageUrl ? (
                            <SheinProductThumb
                              imageUrl={product.imageUrl}
                              className="spo-photo-product__img"
                              placeholderClassName="spo-photo-product__img spo-photo-product__img--empty"
                              alt={product.title || product.skc || ''}
                            />
                          ) : (
                            <div className="spo-photo-product__img spo-photo-product__img--empty" />
                          )}
                          <div className="spo-photo-product__meta">
                            <HoverCopyField
                              value={product.title || ''}
                              className="spo-photo-product__title"
                              popoverCopy
                            />
                            <HoverCopyField
                              label="SPU"
                              value={product.spu}
                              className="spo-photo-product__skc"
                              separator=" "
                            />
                            <HoverCopyField
                              label="SKC"
                              value={product.skc}
                              className="spo-photo-product__skc"
                              separator=" "
                            />
                            {product.supplierCode ? (
                              <HoverCopyField
                                label={t('tiktokCatalog.supplier')}
                                value={product.supplierCode}
                                className="spo-photo-product__skc"
                                separator=" "
                              />
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="spo-certs-td spo-certs-td--wrap">
                        <CellStack
                          items={certificateTypeCodesOf(row)}
                          itemClassName="spo-cell-stack__item spo-certs-type-code"
                        />
                      </td>
                      <td className="spo-certs-td spo-certs-td--wrap">
                        <CellStack items={certificateTypeNamesOf(row)} />
                      </td>
                      <td className="spo-certs-td">
                        <span className={`spo-records__status spo-records__status--${platformStatusModifier(row)}`}>
                          {reviewStatusText(row)}
                        </span>
                      </td>
                      <td className="spo-certs-td spo-certs-td--ops">
                        <div className="spo-drafts__actions">
                          <button
                            type="button"
                            className="spo-btn spo-btn--primary spo-btn--sm"
                            onClick={() => openUpload(row)}
                          >
                            {bindActionLabel(row)}
                          </button>
                          {product.skc && autoBindErrors[product.skc] ? (
                            <button
                              type="button"
                              className="spo-btn spo-btn--secondary spo-btn--sm"
                              onClick={() => setAutoBindErrorSkc(product.skc)}
                            >
                              {t('certList.errorLog')}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('certList.pager')}
        />
      </ProductListCard>

      <ComplianceAutoBindRuleModal
        open={autoBindModalOpen}
        shopId={shopId}
        kind="certificate"
        busy={syncing || loading || deleting || seedingDemo}
        onClose={() => setAutoBindModalOpen(false)}
        onRan={async () => {
          const prefRes = await sheinPlatformAPI.getPreferences().catch(() => null);
          const prefs = prefRes?.preferences || prefRes || {};
          setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'certificate'));
          load().catch(() => {});
        }}
      />
      <CertAutoBindErrorModal
        open={Boolean(autoBindErrorSkc)}
        skc={autoBindErrorSkc}
        reason={autoBindErrors[autoBindErrorSkc] || ''}
        onClose={() => setAutoBindErrorSkc('')}
      />
    </>
  );
}

function CertAutoBindErrorModal({ open, skc, reason, onClose }) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button type="button" className="spo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-cert-auto-bind-error-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-cert-auto-bind-error-title">{t('certList.errorTitle', { skc: skc || '—' })}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          <p className="spo-label-fail__reason">{reason || t('certList.noError')}</p>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onClose}>
            {t('certList.gotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
